import { IconButton, Table, TableBody, Grid, TableCell, TableContainer, TableHead, TableRow, TextField, useTheme, Box, Typography, FormControl, Divider, Button, Autocomplete, alpha } from "@mui/material";
import CustomTableContainer from "../../../components/table/CustomTableContainer";
import SkeletonTableLoader from "../../../components/table/SkeletonTableLoader";
import { useEffect, useState } from "react";
import DownloadIcon from '@mui/icons-material/Download';
import Close from '@mui/icons-material/Close';
import * as XLSX from "xlsx";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import GlobalConfig from "../../../config/date/GlobalConfig";
import dayjs from "dayjs";
import { tokens } from "../../../theme/theme";
import CustomPagination from "../../../components/table/CustomPagination";
import { callApi } from "../../../utils/apiClient";
import { API_ENDPOINTS } from "../../../config/api/apiCall";

// API function to fetch WIP pallet details
const getSheeterPalletDetails = async (payload) => {
  try {
    const endpoint = API_ENDPOINTS.PROD_MANAGEMENT_REPORT_GET_SHEETER_WIP_PALLETS;
    return await callApi(endpoint, "POST", { "Content-Type": "application/json" }, payload);
  } catch (error) {
    console.error("Error fetching WIP pallet details:", error);
    throw error;
  }
};

const ReportSheeterProductionWIPPalletList = ({ handleBackSheeterMachineEntry, mode }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [wipPalletDetails, setWipPalletDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recordsPerPage, setRecordsPerPage] = useState(20);
  const [filterFromDate, setFilterFromDate] = useState(dayjs().subtract(GlobalConfig.defaultDays, "day"));
  const [filterToDate, setFilterToDate] = useState(dayjs());
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    id: "",
    pallet_code: "",
    no_of_sheets: "",
    pallet_weight: "",
    pallet_weight_unit: "",
    reject_status: "",
    size: "",
    status: "",
    storage_status: "",
    id_sheeter_hdr: "",
    lose_pallet_status: "",
    company_id: "",
    plant_id: "",
    created_date_time: "",
  });

  useEffect(() => {
    setCurrentPage(1);
  }, []);

  const loadDataWithDate = async (from, to) => {
    setLoading(true);
    try {
      const p_companyid = localStorage.getItem("companyid");
      const payload = {
        p_companyid: p_companyid,
        p_plantid: 1,
        p_frommodate: dayjs(from).format("YYYY-MM-DD"),
        p_tomodate: dayjs(to).format("YYYY-MM-DD"),
        p_mode: 0,
      };
      const data = await getSheeterPalletDetails(payload);
      setWipPalletDetails(data);
    } catch (error) {
      console.error("Failed to load data with date range:", error);
      setWipPalletDetails([]);
    } finally {
      setLoading(false);
    }
  };

  const loadDataWithoutDate = async () => {
    setLoading(true);
    try {
      const p_companyid = localStorage.getItem("companyid");
      const payload = {
        p_companyid: p_companyid,
        p_plantid: 1,
        p_mode: 0,
      };
      const data = await getSheeterPalletDetails(payload);
      setWipPalletDetails(data);
    } catch (error) {
      console.error("Failed to load data without date range:", error);
      setWipPalletDetails([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dayjs(filterFromDate).isValid() && dayjs(filterToDate).isValid()) {
      loadDataWithDate(filterFromDate, filterToDate);
    }
  }, [filterFromDate, filterToDate]);

  const handleFilterFromDate = (date) => {
    setFilterFromDate(dayjs(date));
  };

  const handleFilterToDate = (date) => {
    setFilterToDate(dayjs(date));
  };

  const handleFilterChange = (e, column) => {
    setFilters({ ...filters, [column]: e.target.value });
  };

  const filteredData = wipPalletDetails.filter((row) => {
    return Object.keys(filters).every((key) => {
      if (filters[key] === "") return true;
      return String(row[key]).toLowerCase().includes(filters[key].toLowerCase());
    });
  });

  const pageCount = Math.ceil(filteredData.length / recordsPerPage);
  const currentRecords = filteredData.slice(
    (currentPage - 1) * recordsPerPage,
    currentPage * recordsPerPage
  );

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleExportToExcel = () => {
    const formattedData = filteredData.map((row) => ({
     "JOB NO": row.jobno || "",
  "PROD. DATE": row.prod_date ? dayjs(row.prod_date).format("DD-MMM-YYYY") : "",
  "SHIFT_CODE": row.shift_code || "",
  "REEL NO": row.reelno || "",
  "PALLET NO": row.palletno || "",
  "QUALITY": row.quality || "",
  "GRADE": row.grade || "",
  "GSM": row.gsm || "",
  "NO OF SHEETS": row.no_of_sheets || "",
  "PALLET WEIGHT": row.pallet_weight || "",
  "ORDER NO": row.orderno || "",
  "WIDTH": row.width || "",
  "LENGTH": row.length1 || "",
  "REMARKS": row.remarks1 || ""
    }));
    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "WIP PALLET DETAILS");
    const fileName = `WIP PALLET DETAILS.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const handleCallBack = () => {
    handleBackSheeterMachineEntry();
  };

  return (
    <>
      <Box
        sx={{
          backgroundColor: theme.palette.background.default,
          minHeight: "100vh",
          p: 2,
        }}
      >
        <Divider />
        <Box marginRight="auto" marginLeft={1}>
            <Typography variant="h4" color={colors.grey[100]} fontWeight="semi bold" >WIP Pallets List</Typography>
          </Box>
        <Grid container padding="5px 0px 5px 0px" alignItems="center" sx={{ mt: 0, mb: 0, pt: 0, pb: 0, justifyContent: "flex-end" }} >
          <Divider orientation="vertical" flexItem sx={{ mx: 0.1 }} />
         
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          <Grid item xs={2} sm={3} md={1.5} lg={1.5} xl={1.2} mt="5px">
            <FormControl fullWidth size="small">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  style={{ marginLeft: '10px' }}
                  id="FromDate"
                  value={dayjs(filterFromDate)}
                  onChange={(date) => handleFilterFromDate(date)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      variant: 'standard',
                      InputProps: {
                        disableUnderline: true,
                      },
                      sx: {
                        backgroundColor: 'transparent',
                        padding: 0,
                        '& .MuiInputBase-root': {
                          border: 'none',
                        },
                        '& .MuiSvgIcon-root': {
                          fontSize: '18px',
                        },
                        '& .MuiInputBase-root': {
                          paddingRight: '6px',
                        },
                      },
                    }
                  }}
                  renderInput={(props) => <TextField {...props} />}
                  autoFocus
                  format="DD-MMM-YYYY"
                />
              </LocalizationProvider>
            </FormControl>
          </Grid>
          <Grid item mt="2px" ml="10px" mr="10px">
            <Typography fontWeight="bold">-</Typography>
          </Grid>
          <Grid item xs={2} sm={3} md={1.5} lg={1.5} xl={1.2} mt="5px" mr="10px">
            <FormControl fullWidth size="small" >
              <LocalizationProvider dateAdapter={AdapterDayjs} style={{ marginLeft: '10px' }}>
                <DatePicker
                  id="ToDate"
                  value={dayjs(filterToDate)}
                  format="DD-MMM-YYYY"
                  onChange={(date) => handleFilterToDate(date)}
                  slotProps={{
                    textField: {
                      size: 'small',
                      variant: 'standard',
                      InputProps: {
                        disableUnderline: true,
                      },
                      sx: {
                        backgroundColor: 'transparent',
                        padding: 0,
                        '& .MuiInputBase-root': {
                          border: 'none',
                        },
                        '& .MuiSvgIcon-root': {
                          fontSize: '18px',
                        },
                        '& .MuiInputBase-root': {
                          paddingRight: '6px',
                        },
                      },
                    }
                  }}
                  renderInput={(props) => <TextField {...props} />}
                />
              </LocalizationProvider>
            </FormControl>
          </Grid>
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          <Grid item mt="2px" ml="5px" mr="5px">
            <Button size="small" variant="contained" color="secondary" onClick={() => { loadDataWithoutDate() }}>Fetch Data Without Date Range</Button>
          </Grid>
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          <Grid item mt="2px" ml="5px" mr="5px">
            <IconButton onClick={handleExportToExcel}>
              <DownloadIcon />
            </IconButton>
          </Grid>
          <Divider orientation="vertical" flexItem />
          {mode === 0 && (
            <>
              <Grid item mt="2px" ml="5px" mr="5px" justifyContent="center">
                <IconButton onClick={() => { handleCallBack() }}>
                  <Close style={{ fontSize: "14px", color: "#2196F3" }} />
                </IconButton>
              </Grid>
              <Divider orientation="vertical" flexItem />
            </>
          ) || (null)}
           {mode === 0 && (
    <IconButton onClick={() => { handleCallBack() }} sx={{ position: "absolute", top: 8, right: 8 }}>
      <Close style={{ fontSize: "14px", color: "#2196F3" }} />
    </IconButton>
  )}
        </Grid>
        <Divider />
        <CustomTableContainer colors={colors}>
          <TableContainer
            sx={{
              maxHeight: '60vh',
              overflowY: 'auto',
            }}
          >
            {loading ? (
              <SkeletonTableLoader rows={10} columns={14} />
            ) : (
              <Table size="small">
                <TableHead sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.1) }}>
                  <TableRow>
                  <TableCell sx={{ textAlign: "center" }}>JOB NO</TableCell>
<TableCell sx={{ textAlign: "center" }}>PROD. DATE</TableCell>
<TableCell sx={{ textAlign: "center" }}>SHIFT_CODE</TableCell>
<TableCell sx={{ textAlign: "center" }}>REEL NO</TableCell>
<TableCell sx={{ textAlign: "center" }}>PALLET NO</TableCell>
<TableCell sx={{ textAlign: "center" }}>QUALITY</TableCell>
<TableCell sx={{ textAlign: "center" }}>GRADE</TableCell>
<TableCell sx={{ textAlign: "center" }}>GSM</TableCell>
<TableCell sx={{ textAlign: "center" }}>NO OF SHEETS</TableCell>
<TableCell sx={{ textAlign: "center" }}>PALLET WEIGHT</TableCell>
<TableCell sx={{ textAlign: "center" }}>ORDER NO</TableCell>
<TableCell sx={{ textAlign: "center" }}>WIDTH</TableCell>
<TableCell sx={{ textAlign: "center" }}>LENGTH</TableCell>
<TableCell sx={{ textAlign: "center" }}>REMARKS</TableCell>

                  </TableRow>
                  {/* Filter Row */}
                  <TableRow>
                    {[
                     "jobno",
                     "prod_date",
                    "shift_code",
                     "reelno",
                     "palletno",
                      "quality",
                      "grade",
                       "gsm",
                      "no_of_sheets",
                      "pallet_weight",
                      "orderno",
                      "width",
                      "length1",
                      "remarks1",
                    ].map((col) => (
                      <TableCell key={col} sx={{ padding: "4px" }}>
                        <TextField
                          variant="standard"
                          value={filters[col] || ''}
                          onChange={(e) => handleFilterChange(e, col)}
                          placeholder="Filter"
                          fullWidth
                          InputProps={{
                            disableUnderline: true,
                          }}
                          sx={{
                            fontSize: '0.75rem',
                            borderRadius: 1,
                            px: 1,
                          }}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {currentRecords.map((row, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        backgroundColor:
                          index % 2 === 0
                            ? theme.palette.background.default
                            : alpha(theme.palette.action.hover, 0.04),
                      }}
                    >
                      <TableCell sx={{ textAlign: "center" }}>{row.jobno}</TableCell>
  <TableCell sx={{ textAlign: "center" }}>{dayjs(row.prod_date).format("DD-MMM-YYYY")}</TableCell>
  <TableCell sx={{ textAlign: "center" }}>{row.shift_code}</TableCell>
  <TableCell sx={{ textAlign: "center" }}>{row.reelno}</TableCell>
  <TableCell sx={{ textAlign: "center" }}>{row.palletno}</TableCell>
  <TableCell sx={{ textAlign: "center" }}>{row.quality}</TableCell>
  <TableCell sx={{ textAlign: "center" }}>{row.grade}</TableCell>
  <TableCell sx={{ textAlign: "center" }}>{row.gsm}</TableCell>
  <TableCell sx={{ textAlign: "center" }}>{row.no_of_sheets}</TableCell>
  <TableCell sx={{ textAlign: "center" }}>{row.pallet_weight}</TableCell>
  <TableCell sx={{ textAlign: "center" }}>{row.orderno}</TableCell>
  <TableCell sx={{ textAlign: "center" }}>{row.width}</TableCell>
  <TableCell sx={{ textAlign: "center" }}>{row.length1}</TableCell>
  <TableCell sx={{ textAlign: "center" }}>{row.remarks1}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <CustomPagination pageCount={pageCount} currentPage={currentPage} onPageChange={handlePageChange} />
          </TableContainer>
        </CustomTableContainer>
      </Box>
    </>
  )
}

export default ReportSheeterProductionWIPPalletList;